import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { getOrders } from '../../services/ordersService';
import './Orders.css';

const ORIGIN_STYLES = {
  'Яндекс.Еда': { bg: '#FFCC00', color: '#1a1a1a' },
  UzumTezkor:   { bg: '#7C3AED', color: '#fff' },
  Wolt:         { bg: '#00C2E8', color: '#0d0d0d' },
  'Web-panel':  { bg: '#f97316', color: '#fff' },
  'Посадка':    { bg: '#10b981', color: '#fff' },
};
const DEFAULT_ORIGIN = { bg: '#6b7280', color: '#fff' };

const PAGE_SIZE = 25;

function formatDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatSum(val) {
  if (val == null) return '—';
  return Number(val).toLocaleString('ru-RU');
}

export default function Orders() {
  const { isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filterOrigin, setFilterOrigin] = useState('');
  const [filterClosed, setFilterClosed] = useState('');
  const [sortDir, setSortDir] = useState('desc');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce the search box so we don't hit the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Any filter change resets to the first page.
  useEffect(() => {
    setPage(1);
  }, [filterClosed, filterOrigin, debouncedSearch]);

  useEffect(() => {
    if (authLoading) return;
    setLoading(true);
    getOrders(page, PAGE_SIZE, {
      closed: filterClosed,
      origin_name: filterOrigin,
      search: debouncedSearch,
    })
      .then(data => {
        setItems(data.items || []);
        setTotal(data.total || 0);
        setTotalPages(data.pages || 1);
      })
      .catch(() => setError('Не удалось загрузить заказы'))
      .finally(() => setLoading(false));
  }, [authLoading, page, filterClosed, filterOrigin, debouncedSearch]);

  // Accumulate origins across loads so the dropdown keeps every platform seen,
  // even after the server filters the list down to one origin.
  const [originSet, setOriginSet] = useState(() => new Set());
  useEffect(() => {
    setOriginSet(prev => {
      const next = new Set(prev);
      items.forEach(o => o.origin_name && next.add(o.origin_name));
      return next.size === prev.size ? prev : next;
    });
  }, [items]);
  const origins = useMemo(() => [...originSet].sort(), [originSet]);

  // Filtering/search now happen on the backend; here we only sort the current
  // page by open_time for the sort toggle.
  const filtered = useMemo(() => {
    return [...items].sort((a, b) => {
      const da = new Date(a.open_time || 0);
      const db = new Date(b.open_time || 0);
      return sortDir === 'desc' ? db - da : da - db;
    });
  }, [items, sortDir]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  if (authLoading || loading) {
    return (
      <div className="orders-page">
        <div className="orders-loading">
          <div className="orders-loading-spinner" />
          <p>Загрузка заказов...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="orders-page">
        <div className="orders-empty">
          <div className="orders-empty-icon">⚠️</div>
          <h2>{error}</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="orders-page">
      <div className="orders-header">
        <div>
          <h1 className="orders-title">Заказы</h1>
          <p className="orders-subtitle">
            {filtered.length !== items.length
              ? `${filtered.length} из ${items.length} · всего ${total}`
              : `Страница ${page} из ${totalPages} · всего ${total} заказов`}
          </p>
        </div>
      </div>

      <div className="orders-filters">
        <input
          className="orders-search"
          placeholder="Поиск по номеру, официанту, филиалу..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select className="orders-select" value={filterOrigin} onChange={e => setFilterOrigin(e.target.value)}>
          <option value="">Все платформы</option>
          {origins.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <select className="orders-select" value={filterClosed} onChange={e => setFilterClosed(e.target.value)}>
          <option value="">Все статусы</option>
          <option value="active">Активные</option>
          <option value="closed">Завершённые</option>
        </select>
        <button
          className="orders-sort-btn"
          onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')}
        >
          Дата {sortDir === 'desc' ? '↓' : '↑'}
        </button>
      </div>

      {filtered.length === 0 ? (
        <div className="orders-empty">
          <div className="orders-empty-icon">🍽️</div>
          <h2>Заказы не найдены</h2>
          <p>Попробуйте изменить фильтры</p>
        </div>
      ) : (
        <>
          <div className="orders-table-wrap">
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Заказ</th>
                  <th>Филиал</th>
                  <th>Платформа</th>
                  <th>Официант</th>
                  <th>Сумма</th>
                  <th>Статус</th>
                  <th>Открыт</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(order => {
                  const style = ORIGIN_STYLES[order.origin_name] || DEFAULT_ORIGIN;
                  const hasDiscount = order.full_sum && order.result_sum && order.result_sum < order.full_sum;
                  return (
                    <tr
                      key={order.id}
                      className="orders-row orders-row-clickable"
                      onClick={() => navigate(`/orders/${order.id}`)}
                    >
                      <td>
                        <div className="orders-num-primary">{order.number || '—'}</div>
                        {order.external_order_number && (
                          <div className="orders-num-secondary">#{order.external_order_number}</div>
                        )}
                      </td>
                      <td className="orders-td-org">{order.organization?.name || '—'}</td>
                      <td>
                        <div className="orders-platform-cell">
                          {order.origin_name ? (
                            <span className="orders-badge" style={{ background: style.bg, color: style.color }}>
                              {order.origin_name}
                            </span>
                          ) : '—'}
                          {order.is_banquet_order && (
                            <span className="orders-tag orders-tag-banquet">Банкет</span>
                          )}
                        </div>
                      </td>
                      <td className="orders-td-waiter">{order.waiter_name || order.cashier_name || '—'}</td>
                      <td>
                        <div className="orders-sum-primary">{formatSum(order.result_sum ?? order.full_sum)} сум</div>
                        {hasDiscount && <div className="orders-sum-old">{formatSum(order.full_sum)} сум</div>}
                      </td>
                      <td>
                        <span className={`orders-status ${order.is_closed_on_ps ? 'orders-status-closed' : 'orders-status-active'}`}>
                          {order.is_closed_on_ps ? 'Завершён' : 'Активный'}
                        </span>
                      </td>
                      <td className="orders-td-date">{formatDate(order.open_time)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="orders-pagination">
            <button className="orders-page-btn" onClick={() => handlePageChange(1)} disabled={page === 1}>«</button>
            <button className="orders-page-btn" onClick={() => handlePageChange(page - 1)} disabled={page === 1}>‹</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                acc.push(p);
                return acc;
              }, [])
              .map((p, i) =>
                p === '...'
                  ? <span key={`dots-${i}`} className="orders-page-dots">…</span>
                  : <button
                      key={p}
                      className={`orders-page-btn ${p === page ? 'orders-page-btn-active' : ''}`}
                      onClick={() => handlePageChange(p)}
                    >{p}</button>
              )}
            <button className="orders-page-btn" onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}>›</button>
            <button className="orders-page-btn" onClick={() => handlePageChange(totalPages)} disabled={page === totalPages}>»</button>
          </div>
        </>
      )}
    </div>
  );
}
